import { Autocomplete } from '@mui/material';
import { useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { MunicipalityVM } from 'system/view-models/municipality';
import { TownHall } from 'shell/models/town-hall';
import { ResponseAbortable } from 'shell/hooks/use-http-fetch/stateless/type';

interface QueryParamsMunicipality extends Record<string, string | boolean | number | undefined> {
  italia?: boolean;
}

interface TownHallAutoProps {
  selectedValue: MunicipalityVM | null;
  handleChange: (value: MunicipalityVM | null) => void;
  handleBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  label: string;
  error?: boolean;
  params?: QueryParamsMunicipality;
  id?: string;
}

const TownHallAutoComplete = ({
  selectedValue,
  handleChange,
  handleBlur = () => {
    return;
  },
  label,
  disabled,
  required = false,
  error,
  id = 'name',
}: TownHallAutoProps): React.ReactElement => {
  const [townHalls, getTownHall] = useHttpFetch<HttpResponseList<TownHall>, Array<MunicipalityVM>>(
    `${__TOPONYMY_API_URL__}/municipi`,
    {
      options: {
        method: 'GET',
        abortable: true,
        errorCatching: false,
        loading: false,
      },
      transformer: (response) => {
        return response.data.payload.list.map((municipality) => ({
          description: municipality.descrizione,
          code: municipality.key.codice,
        }));
      },
    }
  );

  const defaultProps = useMemo(
    () => ({
      options: (townHalls.data as Array<MunicipalityVM>) || [],
      getOptionLabel: (option: MunicipalityVM) => option.description,
    }),
    [townHalls]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        if (townHalls.abort) {
          townHalls.abort();
        }

        getTownHall({
          params: {
            descrizionePostale: value,
          },
        }) as ResponseAbortable<HttpResponseList<TownHall>, Array<MunicipalityVM>>;
      },
      [getTownHall, townHalls.abort]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        handleChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      onBlur={handleBlur}
      clearOnBlur={false}
      loading={townHalls.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

TownHallAutoComplete.displayName = 'Town Hall Auto Complete component';

export default TownHallAutoComplete;
